import copy from 'fast-clone';
import { Action } from 'redux';
import { ofType } from 'redux-observable';
import { concat, Observable, of } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import deepEquals from '../../../Lib/Object/deepEquals';
import isEmptyObject from '../../../Lib/Object/isEmptyObject';
import * as mop from '../../../Lib/Object/Path/Mutable';
import { applyChangeTrackingPatch } from '../../Core/ChangeTracker';
import { IDataSyncModel } from '../../ViewModels/IDataSyncModel';
import { actions } from '../WorksheetEditor';
import { IDataSyncState, ISyncPatch } from './types';

export const getDefaultDataSyncState = (defaultModel = {}, defaultTriggers = {}): IDataSyncState => ({
  syncStatus: 'waiting',
  syncModel: copy(defaultModel),
  syncModel_synced: copy(defaultModel),
  syncModel_conflictClientBefore: null,
  syncParams: copy(defaultTriggers),
  syncParams_synced: copy(defaultTriggers),
});

/**
 * TODO: Remove the necessity for this function.
 *
 * This function is not a great way to manage state updates.
 * Use several separate testable functions that can be called from the individual epics and reducer.
 * I'd like to get rid of the sync patch entirely and maybe only return from the epics the finished result or something.
 */
export const applySyncPatch = (state: IDataSyncState, patch: ISyncPatch) => {
  const { serverModelSent, syncParams, choice } = patch;

  // Cleanup for an unfortunate type decision on these variables
  const serverModelReceived = (isEmptyObject(patch.serverModelReceived) ? undefined : patch.serverModelReceived) as
    | IDataSyncModel
    | undefined;
  const clientModelBefore = (isEmptyObject(patch.clientModelBefore) ? undefined : patch.clientModelBefore) as
    | IDataSyncModel
    | undefined;
  const clientModelAfter = (isEmptyObject(state.syncModel) ? undefined : state.syncModel) as IDataSyncModel | undefined;

  const oPath = mop;

  // Successful sync
  if (clientModelBefore && clientModelAfter && serverModelSent && serverModelReceived) {
    // Execute change tracking patch on (data)
    const { server, client } = applyChangeTrackingPatch({
      clientModelBefore,
      clientModelAfter,
      serverModelSent,
      serverModelReceived,
    });

    // Regular sync
    if (client) oPath.set(state, 'syncModel', client);
    if (server) oPath.set(state, 'syncModel_synced', server);

    // Finally update side effects to reflect the query just completed
    // If side effects have been changed in the mean time, they will be rechecked and will differ.
  }
  // Conflict resolution
  else if (clientModelAfter && serverModelReceived && choice) {
    if (choice === 'client') {
      const client = copy(clientModelAfter);
      client.syncVersionData = serverModelReceived.syncVersionData;
      oPath.set(state, 'syncModel', client);
    } else {
      oPath.set(state, 'syncModel', serverModelReceived);
    }
    state.syncModel_conflictClientBefore = null;
  }
  // GET was successfull but PUT failed, reset to what the GET retrieved
  else if (serverModelReceived) {
    oPath.set(state, 'syncModel', copy(serverModelReceived));
    oPath.set(state, 'syncModel_synced', copy(serverModelReceived));
  }
  // All failed, empty patch. Reset to last known server values
  else {
    oPath.set(state, 'syncModel', copy(state.syncModel_synced));
  }

  if (syncParams) oPath.set(state, 'syncParams_synced', syncParams);

  return state;
};

export const propertyRequiresSync = (state: IDataSyncState, name: string) => {
  name = name.replace(/^(syncModel\.)/, '');
  const { syncModel: clientModelBefore, syncModel_synced: serverModelBefore } = state;
  // Calculate if any changes have been made to syncModel model (and a diff)
  return !deepEquals(mop.get(clientModelBefore, name), mop.get(serverModelBefore, name));
};

/**
 * Returns an observable for use in epics that will emit a sync action, wait for it to be resolved,
 * then emit a resolution.
 *
 * @param action$ The actions observable (from the epic)
 * @param resolve The observable to emit when sync is resolved
 */
export const syncAndAwaitResolve = (action$: Observable<any>, resolve: Observable<Action>) =>
  concat(
    // Emit forced sync action
    of(actions.sync({ force: true })),
    // Listen for the syncResolve action, then resolve
    action$.pipe(
      ofType(actions.syncResolve.toString()),
      take(1),
      switchMap(() => resolve)
    )
  );
